import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Shows charity copy in the cart once the reward spend threshold is reached.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} message - The support message.
 *
 * @extends {Component<Refs>}
 */
class CartChildrenSupport extends Component {
  requiredRefs = ['message'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#recalculate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#recalculate();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.#recalculate());
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-children-support] Event promise rejected:', error);
      });
  };

  #recalculate() {
    const thresholdCents = Number(this.dataset.thresholdCents);
    const message = this.dataset.msg ?? '';
    const root = this.closest('cart-items-component') ?? document;
    let qualifyingCents = 0;

    for (const row of root.querySelectorAll('[data-line-quantity]')) {
      if (!(row instanceof HTMLElement)) continue;
      if (row.dataset.entriesMultiplier === 'true') continue;
      if (row.dataset.rewardItem === 'true') continue;

      const quantity = Number(row.dataset.lineQuantity);
      const unitCents = Number(row.dataset.priceCents);
      if (!Number.isFinite(quantity) || !Number.isFinite(unitCents)) continue;

      qualifyingCents += unitCents * quantity;
    }

    const reached = Number.isFinite(thresholdCents) && qualifyingCents >= thresholdCents && message !== '';
    this.refs.message.textContent = reached ? message : '';
    this.refs.message.toggleAttribute('hidden', !reached);
  }
}

if (!customElements.get('cart-children-support-component')) {
  customElements.define('cart-children-support-component', CartChildrenSupport);
}
